import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import TextCard from './TextCard';

const CategoryContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
`;

const CategoryHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
  padding: 2rem;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  backdrop-filter: blur(10px);
`;

const CategoryTitle = styled.h1`
  font-size: 2.5rem;
  color: white;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
`;

const CategoryDescription = styled.p`
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 1.5rem;
`;

const CategoryStats = styled.div`
  display: flex;
  justify-content: center;
  gap: 2rem;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.8);
`;

const BackButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 2rem;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: translateX(-5px);
  }
`;

const TextGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 1.5rem;
`;

const FilterBar = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  align-items: center;
  flex-wrap: wrap;
`;

const FilterButton = styled.button`
  background: ${props => props.active ? '#667eea' : 'rgba(255, 255, 255, 0.2)'};
  color: ${props => props.active ? 'white' : 'rgba(255, 255, 255, 0.9)'};
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.active ? '#5a6fd8' : 'rgba(255, 255, 255, 0.3)'};
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: rgba(255, 255, 255, 0.8);
`;

const getCategoryDescription = (categoryId) => {
  const descriptions = {
    funny: '搞笑幽默的拍一拍文字，让聊天充满欢声笑语',
    warm: '温暖贴心的拍一拍文字，传递温情与关怀',
    cute: '可爱撒娇的拍一拍文字，增进亲密关系',
    work: '职场专用的拍一拍文字，让工作氛围更轻松',
    festival: '节日庆祝的拍一拍文字，为特殊时刻添彩',
    daily: '日常生活的拍一拍文字，贴心生活小提醒',
    love: '浪漫甜蜜的拍一拍文字，表达爱意与深情',
    friendship: '友情深厚的拍一拍文字，维系珍贵友谊'
  };
  return descriptions[categoryId] || '';
};

const CategoryPage = ({
  categories,
  texts,
  onToggleFavorite,
  isFavorite,
  onCategoryView
}) => {
  const { categoryId } = useParams();
  const [sortBy, setSortBy] = React.useState('default');
  const [randomSeed, setRandomSeed] = React.useState(Math.random());

  const category = categories.find(cat => cat.id === categoryId);

  // 使用 useMemo 包装 categoryTexts 以避免依赖项警告
  const categoryTexts = React.useMemo(() => {
    return texts[categoryId] || [];
  }, [texts, categoryId]);

  useEffect(() => {
    if (onCategoryView && categoryId) {
      onCategoryView(categoryId);
    }
  }, [categoryId, onCategoryView]);

  // 当分类或排序方式改变时重新生成随机种子
  useEffect(() => {
    if (sortBy === 'random') {
      setRandomSeed(Math.random());
    }
  }, [categoryId, sortBy]);

  // 必须在所有条件返回之前调用所有 hooks
  const getSortedTexts = React.useMemo(() => {
    let sorted = [...categoryTexts];

    switch (sortBy) {
      case 'length':
        return sorted.sort((a, b) => a.length - b.length);
      case 'random':
        // 使用Fisher-Yates洗牌算法进行随机排序
        for (let i = sorted.length - 1; i > 0; i--) {
          // 使用randomSeed来生成伪随机索引
          const j = Math.floor((randomSeed * 1000 + i) % (i + 1));
          [sorted[i], sorted[j]] = [sorted[j], sorted[i]];
        }
        return sorted;
      default:
        return sorted;
    }
  }, [categoryTexts, sortBy, randomSeed]);

  const sortedTexts = getSortedTexts;

  if (!category) {
    return (
      <CategoryContainer>
        <EmptyState>
          <h2>分类未找到</h2>
          <p>抱歉，您访问的分类不存在</p>
          <Link to="/">返回首页</Link>
        </EmptyState>
      </CategoryContainer>
    );
  }

  return (
    <CategoryContainer className="fade-in">
      <BackButton to="/">
        ← 返回首页
      </BackButton>

      <CategoryHeader>
        <CategoryTitle>
          {category.icon} {category.name}
        </CategoryTitle>
        <CategoryDescription>
          {getCategoryDescription(categoryId)}
        </CategoryDescription>
        <CategoryStats>
          <span>共 {categoryTexts.length} 条文字</span>
          <span>分类：{category.name}</span>
        </CategoryStats>
      </CategoryHeader>

      <FilterBar>
        <span style={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 'bold' }}>
          排序方式：
        </span>
        <FilterButton
          active={sortBy === 'default'}
          onClick={() => setSortBy('default')}
        >
          默认
        </FilterButton>
        <FilterButton
          active={sortBy === 'length'}
          onClick={() => setSortBy('length')}
        >
          按长度
        </FilterButton>
        <FilterButton
          active={sortBy === 'random'}
          onClick={() => {
            if (sortBy === 'random') {
              // 如果已经是随机排序，重新生成随机种子
              setRandomSeed(Math.random());
            } else {
              setSortBy('random');
            }
          }}
        >
          随机排序
        </FilterButton>
      </FilterBar>

      <TextGrid className="grid-2">
        {sortedTexts.map((text, index) => (
          <TextCard
            key={`${categoryId}-${text.slice(0, 20)}`}
            text={text}
            category={category}
            onToggleFavorite={() => onToggleFavorite(text, categoryId)}
            isFavorite={isFavorite(text)}
          />
        ))}
      </TextGrid>

      {sortedTexts.length === 0 && (
        <EmptyState>
          <h3>暂无内容</h3>
          <p>这个分类还没有文字，敬请期待！</p>
        </EmptyState>
      )}
    </CategoryContainer>
  );
};

export default CategoryPage;
